import { AttioDealAttributes, AttioDealContractListQueryResponse, AttioDealResponse, AttioDealStatusListQueryResponse, AttioListQueryRequest, CurrencyValue, TextValue } from '../attio.types';
import { BaseAttioService } from './BaseAttioService';
import formatCurrency from '../../utilities/formatCurrency';

// Service-specific types
export interface AttioDealData {
  signedPDF: string | undefined;
  dealDate: string | undefined;
  net: string | undefined;
  dealId: string;
  dealName: string;
  dealUrl: string;
  dealTotal: string;
  dealStage: string;
  due: string;
  engagementManagerId?: string;
  notice?: TextValue[];
}

export class AttioDealService extends BaseAttioService {
  constructor() {
    super('AttioDealService');
  }

  /**
   * Get deal details by record ID
   */
  async getDealDetail(dealRecordId: string): Promise<AttioDealResponse> {
    try {
      const response = await this.get<AttioDealResponse>(this.endpoints.retrieveDealByRecordId(dealRecordId));
      return response;
    } catch (error) {
      this.handleError(error, 'getDealDetail');
    }
  }

  /**
   * Get deal data with extracted information
   */
  async getDealData(dealRecordId: string): Promise<AttioDealData> {
    try {
      const response = await this.getDealDetail(dealRecordId);
      const dealData = response.data.values;
      const { value: DealValue, due: DealDue } = dealData as AttioDealAttributes;
      const DealFormatValue = DealValue?.[0] as CurrencyValue;
      const { currency_value: DealTotalValue, currency_code: DealCurrencyCode } = DealFormatValue;

      const DealFormatDue = DealDue?.[0] as CurrencyValue;
      const { currency_value: DealDueValue, currency_code: DealDueCurrencyCode } = DealFormatDue;

      return {
        dealId: dealRecordId,
        dealName: dealData.name?.[0]?.value || '-',
        dealUrl: response.data.web_url,
        dealTotal: DealTotalValue ? formatCurrency({ value: DealTotalValue, currency: DealCurrencyCode || 'USD' }) : '-',
        due: DealDueValue ? formatCurrency({ value: DealDueValue, currency: DealDueCurrencyCode || 'USD' }) : '-',
        dealStage: dealData.stage?.[0]?.status?.title || '-',
        engagementManagerId: dealData?.engagement_manager?.[0]?.referenced_actor_id,
        notice: dealData.notice,
        dealDate: dealData.deal_date[0]?.value || undefined,
        net: dealData.net[0]?.currency_value || undefined,
      };
    } catch (error) {
      this.handleError(error, 'getDealData');
    }
  }

  async getDealContractData(dealRecordId: string, listIdOrSlug: string = 'contracts'): Promise<AttioDealContractListQueryResponse> {
    try {
      // Use the correct Attio API structure with path and constraints
      const queryRequest: AttioListQueryRequest = {
        'filter': {
          'path': [
            ['contracts', 'parent_record']
          ],
          'constraints': {
            'target_object': 'deals',
            'target_record_id': dealRecordId
          }
        }
      };
      
      const response = await this.post<AttioDealContractListQueryResponse>(
        this.endpoints.retrieveDealContractByRecordId(listIdOrSlug),
        queryRequest
      );
      return response;
    } catch (error) {
      console.error('Error in getDealContractData:', error);
      this.handleError(error, 'getDealContractData');
    }
  }

  /**
   * Get deal contract data using the specific list ID from your curl command
   */
  async getDealContractDataWithSpecificList(dealRecordId: string): Promise<AttioDealContractListQueryResponse> {
    // Use the specific list ID from your curl command
    const listId = '31d5ad4c-1c71-4c20-985a-3f4119b052ae';
    return this.getDealContractData(dealRecordId, listId);
  }

  async getDealStatuses(): Promise<AttioDealStatusListQueryResponse> {
    const response = await this.get<AttioDealStatusListQueryResponse>(this.endpoints.retrieveDealStatuses());
    return response;
  }

  async updateValuationDealStage({dealId, dealStageOptionId}: {dealId: string, dealStageOptionId: string}): Promise<void> {
    const response = {
      data: {
        values: {
          stage: [
            {
              status: dealStageOptionId,
            },
          ],
        },
      },
    }
    await this.patch(this.endpoints.updateValuationDealStage(dealId), response);
  }
}
